import { useCallback, useEffect, useRef, useState } from "react";
import { organizationAccessClient } from "server/devplane/OrganizationAccesses";

export type TeamMember = { id: string; name: string };

export function useOrganizationTeamMembers(organizationId: string) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const requestIdRef = useRef(0);

  const loadTeamMembers = useCallback(
    async (clearOnFailure = false) => {
      const requestId = ++requestIdRef.current;
      if (!organizationId) {
        if (requestId === requestIdRef.current) setTeamMembers([]);
        return;
      }

      try {
        const response =
          await organizationAccessClient.listMembers(organizationId);
        if (requestId !== requestIdRef.current) return;

        setTeamMembers(
          response.members.map((member) => ({
            id: member.userId,
            name: member.displayName || member.defaultEmail || member.userId,
          })),
        );
      } catch (error) {
        if (clearOnFailure && requestId === requestIdRef.current) {
          setTeamMembers([]);
        }
        throw error;
      }
    },
    [organizationId],
  );

  useEffect(() => {
    // The state update happens after the member request settles, not during the effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadTeamMembers(true).catch(() => undefined);
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadTeamMembers]);

  return { loadTeamMembers, teamMembers };
}
